import * as React from 'react'

import { MatchRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@enechain/auth-js'
import {
  SideMenuLayout,
  SideMenuLayoutAppSwitcherMenu,
  SideMenuLayoutAppSwitcherMenuProps,
  SideMenuLayoutMenuItem,
  SideMenuLayoutPopOverMenuItem,
  SideMenuLayoutSecondaryMenuProvider,
  SideMenuLayoutUserMenu,
  useSideMenuCollapsed,
} from '@enechain/ecloud-designsystem'
import {
  ECloudHorizontalWithYohaku,
  ECloudSymbol,
  EnecAppswitcherRegular,
  FaAddressCardRegular,
  FaCircleQuestionRegular,
  FaFileRegular,
  FaHouseRegular,
  FaShieldKeyholeRegular,
  FaUserRegular,
} from '@enechain/ecloud-icons'

import { OrganizationAdmin } from '~/components/authorize/OrganizationAdmin'
import { isApiKeysEnabled, isEbillingEnabled } from '~/constants/featureFlag'
import { useLoginOrganization } from '~/hooks/useLoginOrganization'
import { useLoginUser } from '~/hooks/useLoginUser'
import { useListProducts } from '~/pages/Home/hooks/useProduct'

import FullLoadingView from './FullLoadingView'
import SideMenuLayoutOrganization from './SideMenuLayoutOrganization'

type AppSwitcherName =
  SideMenuLayoutAppSwitcherMenuProps['items'][number]['name']

export const Logo: React.FC = () => {
  const { collapsed } = useSideMenuCollapsed()

  if (collapsed) {
    return <ECloudSymbol height="30px" marginTop="-4.5px" aria-hidden />
  }
  return (
    <ECloudHorizontalWithYohaku
      width="108px"
      height="32px"
      marginTop="-4px"
      marginLeft="-5px"
      aria-hidden
    />
  )
}

export const TopMenu: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const organization = useLoginOrganization()
  return (
    <React.Suspense fallback={<FullLoadingView />}>
      <SideMenuLayoutOrganization
        name={organization.name}
        src={organization.imageUrl + '?' + new Date().toISOString()}
      />
      <SideMenuLayoutMenuItem
        IconLeft={FaHouseRegular}
        isActive={MatchRoute({ to: '/', fuzzy: true }) === undefined}
        label={t('pages.home.title')}
        onClick={() => {
          void navigate({
            to: '/',
          })
        }}
      />
      <SideMenuLayoutMenuItem
        IconLeft={FaUserRegular}
        isActive={MatchRoute({ to: '/members', fuzzy: true }) === undefined}
        label={t('pages.members.list')}
        onClick={() => {
          void navigate({
            to: '/members',
          })
        }}
      />
      {/* ebilling 連携が無効な環境では請求メニューを表示しない */}
      {isEbillingEnabled && (
        <SideMenuLayoutMenuItem
          IconLeft={FaFileRegular}
          isActive={MatchRoute({ to: '/billing', fuzzy: true }) === undefined}
          label={t('pages.billing.title')}
          onClick={() => {
            void navigate({
              to: '/billing',
            })
          }}
        />
      )}
      {isApiKeysEnabled && (
        <OrganizationAdmin>
          <SideMenuLayoutMenuItem
            IconLeft={FaShieldKeyholeRegular}
            isActive={
              MatchRoute({ to: '/api_keys', fuzzy: true }) === undefined
            }
            label={t('pages.api_keys.title')}
            onClick={() => {
              void navigate({
                to: '/api_keys',
              })
            }}
          />
        </OrganizationAdmin>
      )}
      <SideMenuLayoutMenuItem
        IconLeft={FaAddressCardRegular}
        isActive={MatchRoute({ to: '/profile', fuzzy: true }) === undefined}
        label={t('pages.profile.title')}
        onClick={() => {
          void navigate({
            to: '/profile',
          })
        }}
      />
    </React.Suspense>
  )
}

export const BottomMenu: React.FC = () => {
  const { logout } = useAuth()
  const { t } = useTranslation()
  const me = useLoginUser()
  const products = useListProducts()
  const onClickSignOut = (): void => {
    logout({ returnTo: window.location.origin })
  }
  return (
    <>
      <SideMenuLayoutMenuItem
        IconLeft={FaCircleQuestionRegular}
        label={t('pages.home.label.helpCenter')}
        onClick={(): void => {
          window.open(import.meta.env.VITE_SUPPORT_PAGE_URL, '_blank')
        }}
      />
      <SideMenuLayoutAppSwitcherMenu
        Icon={EnecAppswitcherRegular}
        items={products.map((product) => {
          return {
            name: product.name as AppSwitcherName,
            onClick: (): void => {
              window.open(product.url, '_blank')
            },
          }
        })}
        label={t('pages.home.label.appSwitcher')}
      />
      <SideMenuLayoutUserMenu
        popOverMenu={
          <SideMenuLayoutPopOverMenuItem
            label={t('action.logout')}
            onClick={onClickSignOut}
          />
        }
        userIconSrc={me.imageUrl + '?' + new Date().toISOString()}
        userName={me.name}
      />
    </>
  )
}

export const MenuLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <React.Suspense fallback={<FullLoadingView />}>
      <SideMenuLayoutSecondaryMenuProvider>
        <SideMenuLayout
          bottomMenu={BottomMenu}
          defaultCollapsed={false}
          defaultMenuLock
          logo={Logo}
          topMenu={TopMenu}
        >
          {children}
        </SideMenuLayout>
      </SideMenuLayoutSecondaryMenuProvider>
    </React.Suspense>
  )
}
